<script setup lang="ts">
  import { Toaster, useToast } from '@gat-ship/vue-components'
  import type { IconComponent } from '@gat-ship/icons'
  import { notificationSink } from '@gat-ship/error-handling'
  import { watch } from 'vue'
  import { useNotificationDecisions } from '../composables/useNotificationDecisions'

  const { toast } = useToast()
  const { variantForLevel, isAlreadyProcessed } = useNotificationDecisions()

  /**
   * Dedupe within a single synchronous burst of sink pushes. When two
   * `notificationSink.reportError(...)` calls fire in the same tick, Vue
   * batches them and this watcher is invoked once with both toasts in
   * `state.toasts` — we need `processed` to ensure each toast is handed
   * off to the Toaster exactly once. After the microtask below drains
   * them from the sink, the processed id is deleted, so the Set remains
   * bounded to whatever's in flight right now (usually 0–1 entries).
   *
   * The older "don't leak across SPA lifetime" concern no longer applies
   * because the microtask drain keeps both `state.toasts` AND this Set
   * from growing.
   */
  const processed = new Set<string>()

  watch(
    () => notificationSink.state.toasts,
    (toasts) => {
      for (const t of toasts) {
        if (isAlreadyProcessed(processed, t.id)) continue
        processed.add(t.id)
        toast({
          title: t.title,
          description: t.description,
          icon: t.icon as IconComponent | undefined,
          variant: variantForLevel(t.level)
        })
        // Drain from the sink in a microtask to avoid re-entering the watcher
        // synchronously, and drop the processed id in the same step so the
        // Set doesn't grow for the lifetime of the SPA.
        const id = t.id
        queueMicrotask(() => {
          notificationSink.dismissToast(id)
          processed.delete(id)
        })
      }
    },
    { deep: true, immediate: true }
  )
</script>

<template>
  <Toaster />
  <slot />
</template>
